// Modelo de negocio del paso 2 del wizard de captura.
// Centraliza constantes, cálculos y la forma serializable a JSON usada por la
// UI para mostrar montos. La fórmulas viven aquí — no duplicarlas en JSX.

export const PRELIMINAR_MXN_POR_HA = 1250;
export const CONSTRUCCION_PLAZO_DEFAULT_ANIOS = 25;
export const CONSTRUCCION_TARIFA_DEFAULT_MXN_POR_UNIDAD = 25_000;

export const MODELO_NEGOCIO_VERSION = 2 as const;

/**
 * Esquema de diferimiento mensual del subtotal de construcción y operación.
 *
 * UX simplificado (mayo 2026): antes se capturaban N tramos arbitrarios
 * (concepto + monto). El feedback de campo fue que en la práctica todos
 * los acuerdos se diferían a un número fijo de mensualidades; los tramos
 * libres generaban más fricción que valor. Ahora un solo control:
 * número de meses → mensualidad calculada. El total no cambia, solo la
 * cadencia de pago.
 */
export interface ModeloNegocioPagosDiferidos {
  /** Cantidad de mensualidades. null o 0 = al contado (un solo pago). */
  meses: number | null;
  /** subtotal_construccion / meses cuando ambos son válidos; null si no aplica. */
  monto_mensual_mxn: number | null;
  /** Toggle de seguimiento mensual al cliente. Solo metadata — la UI lo muestra
   *  como pista discreta debajo del control. */
  seguimiento_mensual: boolean;
}

export interface ModeloNegocioPreliminar {
  superficie_ha: number | null;
  tarifa_mxn_por_ha: number;
  monto_mxn: number | null;
}

export interface ModeloNegocioBonoFirma {
  monto_mxn: number | null;
}

export interface ModeloNegocioConstruccion {
  plazo_anios: number;
  tarifa_mxn_por_unidad: number;
  cantidad_unidades: number | null;
  subtotal_mxn: number | null;
}

export interface ModeloNegocioTotales {
  suma_componentes_mxn: number | null;
}

export interface ModeloNegocio {
  version: typeof MODELO_NEGOCIO_VERSION;
  moneda: 'MXN';
  preliminar: ModeloNegocioPreliminar;
  bono_firma: ModeloNegocioBonoFirma;
  construccion_operacion: ModeloNegocioConstruccion;
  pagos_diferidos: ModeloNegocioPagosDiferidos;
  totales: ModeloNegocioTotales;
}

export interface ModeloNegocioInput {
  preliminar_superficie_ha: number | null;
  bono_firma_mxn: number | null;
  construccion_plazo_anios: number;
  construccion_tarifa_mxn_por_unidad: number;
  construccion_cantidad_unidades: number | null;
  /** Mensualidades para diferir el subtotal de construcción.
   *  null o 0 → al contado (no hay diferimiento). */
  diferir_meses: number | null;
  /** Toggle de seguimiento mensual (solo metadata, no afecta cálculos). */
  seguimiento_mensual: boolean;
}

function isFiniteNumber(n: number | null | undefined): n is number {
  return typeof n === 'number' && Number.isFinite(n);
}

/**
 * Construye el `modelo_negocio` desde el estado del formulario.
 *
 * Reglas de cálculo (única fuente de verdad):
 *  - preliminar.monto_mxn = superficie_ha × tarifa_mxn_por_ha cuando superficie_ha es número válido.
 *  - construccion_operacion.subtotal_mxn = tarifa × cantidad cuando ambos son números válidos.
 *  - totales.suma_componentes_mxn = suma de los 3 componentes; null se trata como 0.
 *    Si los 3 componentes son null, el total queda null (no fuerza un 0 espurio).
 *  - pagos_diferidos: si `diferir_meses > 0` y hay subtotal de construcción,
 *    `monto_mensual_mxn = subtotal / meses`. El total no cambia, solo la cadencia.
 */
export function buildModeloNegocio(input: ModeloNegocioInput): ModeloNegocio {
  const superficie = input.preliminar_superficie_ha;
  const preliminarMonto = isFiniteNumber(superficie)
    ? superficie * PRELIMINAR_MXN_POR_HA
    : null;

  const tarifaUnidad = input.construccion_tarifa_mxn_por_unidad;
  const cantidadUnidades = input.construccion_cantidad_unidades;
  const subtotalConstruccion =
    isFiniteNumber(tarifaUnidad) && isFiniteNumber(cantidadUnidades)
      ? tarifaUnidad * cantidadUnidades
      : null;

  const componentes: Array<number | null> = [
    preliminarMonto,
    input.bono_firma_mxn,
    subtotalConstruccion,
  ];
  const algunoEsNumero = componentes.some(isFiniteNumber);
  const sumaComponentes = algunoEsNumero
    ? componentes.reduce<number>((acc, n) => acc + (isFiniteNumber(n) ? n : 0), 0)
    : null;

  // Diferimiento mensual: solo aplica si meses > 0 y hay subtotal de construcción.
  const meses = isFiniteNumber(input.diferir_meses) && input.diferir_meses > 0
    ? Math.round(input.diferir_meses)
    : null;
  const montoMensual = meses != null && isFiniteNumber(subtotalConstruccion)
    ? subtotalConstruccion / meses
    : null;

  return {
    version: MODELO_NEGOCIO_VERSION,
    moneda: 'MXN',
    preliminar: {
      superficie_ha: isFiniteNumber(superficie) ? superficie : null,
      tarifa_mxn_por_ha: PRELIMINAR_MXN_POR_HA,
      monto_mxn: preliminarMonto,
    },
    bono_firma: {
      monto_mxn: isFiniteNumber(input.bono_firma_mxn) ? input.bono_firma_mxn : null,
    },
    construccion_operacion: {
      plazo_anios: input.construccion_plazo_anios,
      tarifa_mxn_por_unidad: tarifaUnidad,
      cantidad_unidades: isFiniteNumber(cantidadUnidades) ? cantidadUnidades : null,
      subtotal_mxn: subtotalConstruccion,
    },
    pagos_diferidos: {
      meses,
      monto_mensual_mxn: montoMensual,
      seguimiento_mensual: !!input.seguimiento_mensual,
    },
    totales: {
      suma_componentes_mxn: sumaComponentes,
    },
  };
}

const MXN_FORMATTER_SMART = new Intl.NumberFormat('es-MX', {
  style: 'currency',
  currency: 'MXN',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});
const MXN_FORMATTER_DECIMAL = new Intl.NumberFormat('es-MX', {
  style: 'currency',
  currency: 'MXN',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Formato accounting MXN: enteros sin decimales ("$12,500"), no-enteros con
 * dos decimales ("$12,500.50"). Usar para outputs y displays del modelo.
 * Pasar `placeholderEmpty: ''` para inputs vacíos; default '—' para outputs.
 */
export function formatMxn(value: number | null | undefined, placeholderEmpty: string = '—'): string {
  if (!isFiniteNumber(value)) return placeholderEmpty;
  return Number.isInteger(value) ? MXN_FORMATTER_SMART.format(value) : MXN_FORMATTER_DECIMAL.format(value);
}

/**
 * Superficie en ha para inputs/readouts: separador decimal punto (no moneda).
 * Hasta 4 decimales, sin ceros finales innecesarios.
 */
export function formatHa(value: number | null | undefined, placeholderEmpty: string = ''): string {
  if (!isFiniteNumber(value)) return placeholderEmpty;
  const n = Math.round(value * 10_000) / 10_000;
  let s = n.toString();
  if (s.includes('e')) s = n.toFixed(4);
  if (!s.includes('.')) return s;
  return s.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
}

/** Interpreta texto de ha: admite coma o punto como decimal. */
export function parseHaInput(raw: string): number | null {
  const t = raw.trim().replace(/\s/g, '').replace(',', '.');
  if (t === '') return null;
  const n = Number(t);
  if (!Number.isFinite(n) || n < 0) return null;
  return n;
}

/* ────────────────────────────────────────────────────────────────────────── */
/*  Export CSV del Resumen económico                                          */
/* ────────────────────────────────────────────────────────────────────────── */

/** Identifica la parcela en el header del export (PDF + Excel). Todos los campos son opcionales. */
export interface ResumenEconomicoContext {
  parcela_id?: string | null;       // ej. "HUM-P1126"
  num_parcela?: string | number | null;  // ej. 103
  nom_nucleo?: string | null;
  municipio?: string | null;
  area_ha?: number | null;
  /**
   * Fecha estimada de firma del contrato — string ISO `YYYY-MM-DD` tal como
   * lo guarda `<input type="date">`. Hito comercial clave: aparece destacado
   * en el PDF y como row dedicada en el Excel.
   */
  fecha_firma_est?: string | null;
  /**
   * Identificadores agrarios + cadastrales de la parcela. Se exponen en el
   * Excel "contable" para que el cliente tenga todo en un solo archivo.
   * Política PII: `titular_masked` es plaintext seguro (mismo criterio que
   * el PDF cliente / `v_inventario`). Folios y clave catastral son
   * identificadores no-PII en este contexto.
   */
  folio_derechos?: string | null;
  folio_tierras?: string | null;
  clave_catastral?: string | null;
  titular_masked?: string | null;
  /**
   * PII descifrada del titular — solo se llena cuando el técnico tiene MFA
   * reciente y `useCapturaHydrateUnmasked` ya devolvió datos. Si está
   * presente, el PDF/Excel los embebe en el bloque "Datos del titular";
   * caso contrario el documento cae a `titular_masked`. El descifre lo
   * audita el BFF (`propietario.unmask_for_hydration` en eventos_negociacion).
   */
  titular_completo?: string | null;
  telefono?: string | null;
}

